import { PageHeader } from "components/ui";
import React from 'react';
import { Grid, Typography, Box } from "@mui/material";
import { fontFamily, fontSize } from '../../../node_modules/@mui/system/index';
import CustomButton from 'components/buttonsComponent/StyledButton';
import NavigationButtons from 'helper/navigation/NavigationButtons';

const DoubleMateriality = () => {
    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PageHeader
                        title="AI-Powered ESG Reporting across Global, National, Sectoral, and Industry-Specific Standards"
                    />
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Box className="gri-header">
                    <NavigationButtons imageKey="CSRD/ESRS (EU)" title={"CSRD-ESRS Reporting"} activeStep={0} />
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box
                    sx={{
                        borderRadius: '8px',
                        padding: '24px',
                        top: '226px',
                        left: '75px',
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: '#2E7D32',
                            mb: 3,
                            fontFamily: 'DM Sans',
                            fontSize: '40px',
                            lineHeight: '38px',
                        }}
                    >
                        The global standards for sustainability<br /> impacts
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                        <Typography
                            component="h4"
                            sx={{
                                fontFamily: 'DM Sans',
                                fontWeight: 800,
                                fontSize: '20px',
                                lineHeight: '28px',
                                letterSpacing: '-2%',
                                color: '#4F4F4F',
                                mb: 1,
                            }}
                        >
                            What is CSRD? — Brief introduction
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                fontFamily: 'DM Sans',
                                fontWeight: 400,
                                fontSize: '20px',
                                lineHeight: '28px',
                                letterSpacing: '-2%',
                                color: '#4F4F4F',
                            }}
                        >
                            The Corporate Sustainability Reporting Directive (CSRD) is a European Union (EU) regulation that significantly expands sustainability reporting requirements for companies operating in or linked to the EU.
                        </Typography>
                    </Box>

                    <Typography
                        variant="body1"
                        sx={{
                            fontFamily: 'DM Sans',
                            fontWeight: 400,
                            fontSize: '20px',
                            lineHeight: '28px',
                            letterSpacing: '-2%',
                            color: '#4F4F4F',
                            mb: 2,
                        }}
                    >
                        It replaces the Non-Financial Reporting Directive (NFRD) and applies to a much larger number of companies, including non-European companies with significant EU activities.
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            fontFamily: 'DM Sans',
                            fontWeight: 400,
                            fontSize: '20px',
                            lineHeight: '28px',
                            letterSpacing: '-2%',
                            color: '#4F4F4F',
                            mb: 4,
                        }}
                    >
                        CSRD requires detailed, auditable disclosures on environmental, social, and governance (ESG) matters, aligned with European Sustainability Reporting Standards (ESRS). It aims to ensure transparency for investors, regulators, and stakeholders about companies’ sustainability risks, opportunities, and impacts.
                    </Typography>

                    {/* 💥 Double Materiality Section Starts Here */}
                    <Typography
                        component="h4"
                        sx={{
                            fontFamily: 'DM Sans',
                            fontWeight: 800,
                            fontSize: '20px',
                            lineHeight: '28px',
                            letterSpacing: '-2%',
                            color: '#4F4F4F',
                            mb: 1,
                        }}
                    >
                        What is Double Materiality? (Impact + Financial Materiality)
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            fontFamily: 'DM Sans',
                            fontWeight: 400,
                            fontSize: '20px',
                            lineHeight: '28px',
                            letterSpacing: '-2%',
                            color: '#4F4F4F',
                            mb: 2,
                        }}
                    >
                        Double materiality is a key reporting concept under CSRD and ESRS, combining two perspectives:
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            fontFamily: 'DM Sans',
                            fontWeight: 600,
                            fontSize: '20px',
                            lineHeight: '28px',
                            letterSpacing: '-2%',
                            color: '#4F4F4F',
                            mb: 1,
                        }}
                    >
                        1️⃣ Impact Materiality (Inside-Out)
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            fontFamily: 'DM Sans',
                            fontWeight: 400,
                            fontSize: '20px',
                            lineHeight: '28px',
                            letterSpacing: '-2%',
                            color: '#4F4F4F',
                            mb: 2,
                        }}
                    >
                        This assesses how the company’s activities impact the environment, people, and society — for example, GHG emissions, water use, human rights, or community effects. It focuses on the company’s outward footprint and sustainability consequences, even if they don’t directly affect financial performance.
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            fontFamily: 'DM Sans',
                            fontWeight: 600,
                            fontSize: '20px',
                            lineHeight: '28px',
                            letterSpacing: '-2%',
                            color: '#4F4F4F',
                            mb: 1,
                        }}
                    >
                        2️⃣ Financial Materiality (Outside-In)
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            fontFamily: 'DM Sans',
                            fontWeight: 400,
                            fontSize: '20px',
                            lineHeight: '28px',
                            letterSpacing: '-2%',
                            color: '#4F4F4F',
                            mb: 2,
                        }}
                    >
                        This assesses how sustainability risks and opportunities affect the company’s financial position — for example, climate risks, regulatory changes, supply chain disruptions, or reputational risks. It focuses on what threatens or benefits the company’s value creation, cash flows, and resilience.
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            fontFamily: 'DM Sans',
                            fontWeight: 500,
                            fontSize: '20px',
                            lineHeight: '28px',
                            letterSpacing: '-2%',
                            color: '#4F4F4F',
                        }}
                    >
                        ✅ Double materiality means companies must report on both:
                        <ul style={{ paddingLeft: '1.5rem', marginTop: '8px', marginBottom: '8px' }}>
                            <li>Their impact on the world (impact materiality)</li>
                            <li>The world’s impact on them (financial materiality)</li>
                        </ul>
                        This approach ensures that sustainability reporting addresses both corporate accountability and investor-relevant financial risks, supporting a holistic view of sustainability performance.
                    </Typography>
                </Box>

                {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <CustomButton title="Access Full GRI Standards Documentation"
                        route="/reporting/whatisgri"
                    />
                </Box> */}
            </Grid>
        </Grid>
    );
};

export default DoubleMateriality;